// RHFAutocompleteCorrespondants.jsx
import React from "react";
import { useFormContext, Controller, useController } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import { correspondantsIsLoadingSelector, makeCorrespondantsSelector } from "../../redux/Correspondants/selectors";
import { getCorrespondantsRoutine } from "../../redux/Correspondants/routines";
import { correspondantFormActionCreator } from "./forms/correspondantListActionCreator";
import { correspondantsSplitDialogActionCreator } from "./forms/correspondantSplitViewFormActionCreator";



const filter = createFilterOptions();

export default function RHFAutocompleteCorrespondants({ name, control, alertId, ...rest}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const correspondantsSelector = React.useMemo(makeCorrespondantsSelector, []);
  const correspondantsRaw = useSelector(correspondantsSelector);
  const loading = useSelector(correspondantsIsLoadingSelector);

  const correspondants = React.useMemo(() => {
    if (!correspondantsRaw) return [];
    return [...correspondantsRaw].filter(c => !c?.archive).sort((a, b) => {
      const catA = a.category || "zzzz"; // 🔑 force "Sans Catégorie" à passer après
      const catB = b.category || "zzzz";

      const cmpCat = catA.localeCompare(catB, "fr", { sensitivity: "base" });
      if (cmpCat !== 0) return cmpCat;

      // même catégorie → tri par lastname + firstname
      const nameA = `${a.lastname || ""} ${a.firstname || ""}`.trim().toLowerCase();
      const nameB = `${b.lastname || ""} ${b.firstname || ""}`.trim().toLowerCase();
      return nameA.localeCompare(nameB, "fr", { sensitivity: "base" });
    });
  }, [correspondantsRaw]);

  React.useEffect(() => {
    dispatch(getCorrespondantsRoutine.trigger());
  }, [dispatch]);

  // RHF → value = tableau d’IDs (string[])
  const {
    field: { value = [], onChange },
  } = useController({ name, control, defaultValue: [] });

  // conversion IDs → objets pour Autocomplete
  const selectedObjects = React.useMemo(() => {
    const map = new Map(correspondants.map(c => [c.id, c]));
    return value.map(id => map.get(id)).filter(Boolean);
  }, [value, correspondants]);

  const getOptionLabel = React.useCallback((option) => {
    if (typeof option === "object" && option !== null && !Array.isArray(option)) {
      return `${option?.lastname || ""} ${option?.firstname || ""}`.trim();
    } else if (typeof option === "string") {
      const c = correspondants.find(c => c.id === option);
      return `${c?.lastname || ""} ${c?.firstname || ""}`.trim();
    } else if (Array.isArray(option)) {
      return option.map(o => (typeof o === "string" ? o : o.id)).join(", ");
    }
    return "";
  }, [correspondants]);

  const getOptionSelected = (option, val) => {
    if (typeof val === "object" && val !== null) return option.id === val.id;
    if (typeof val === "string") return option.id === val;
    return false;
  };

  const filterOptions = (options, params) => {
    const filtered = filter(options, params);
    if (params.inputValue !== "" && !options.includes(params.inputValue)) {
      filtered.push({
        inputValue: params.inputValue,
        lastname: t("common:interface.add"),
        firstname: `"${params.inputValue}"`,
      });
    }
    return filtered;
  };

  const InputProps = React.useMemo(
    () => ({
      endAdornment: (
        <>
          {loading ? <CircularProgress color="inherit" size={20} /> : null}
        </>
      ),
    }),
    [loading]
  );

  return (
    <Autocomplete
      multiple
      disableClearable
      includeInputInList
      filterSelectedOptions
      options={correspondants}      // objets
      value={selectedObjects}       // objets
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      filterOptions={filterOptions}
      loading={loading}
      groupBy={(option) => option.category || t('common:interface.Sans Catégorie')}
      onChange={(event, newObjects, reason) => {
        if (reason === "select-option") {
          const last = newObjects[newObjects.length - 1];
          if (last?.inputValue) {
            dispatch(
              correspondantsSplitDialogActionCreator({ lastname: last.inputValue }, alertId, (err, payload) => {
                if (err) return;
                if (payload?.action === "post") {
                  onChange([...(value || []), payload.id]); // ajoute l’ID
                }
              })
              // correspondantFormActionCreator({ lastname: last.inputValue }, alertId, (err, payload) => {
              //   if (err) return;
              //   if (payload?.action === "post") {
              //     onChange([...(value || []), payload.id]); // ajoute l’ID
              //   }
              // })
            );
            return;
          }
        }
        const ids = newObjects.map(o => (typeof o === "object" ? o.id : o)).filter(Boolean);
        onChange(ids); // ne stocke que les IDs
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            style={{margin: 0}}
            size="small"
            variant="outlined"
            label={getOptionLabel(option)}
            onClick={() => {
              const toDeleteId = typeof option === "object" ? option.id : option;
              const found = correspondants.find(c => c.id === toDeleteId);
              if (!found) return;
              dispatch(
                correspondantsSplitDialogActionCreator(found, alertId, (err, payload) => {
                  if (err) return;
                  if (payload?.action === "delete") {
                    onChange(value.filter(id => id !== payload.id));
                  }
                })
                // correspondantFormActionCreator(found, alertId, (err, payload) => {
                //   if (err) return;
                //   if (payload?.action === "delete") {
                //     onChange(value.filter(id => id !== payload.id));
                //   }
                // })
              );
            }}
            {...getTagProps({ index })}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          InputProps={{ ...params.InputProps, ...InputProps, style: {gap: 4}}}
          {...rest}
        />
      )}
    />
  );
}